import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import EnvQueryBuilderView from './env-query-builder/EnvQueryBuilderView';
import ConsistFilter from './filters/ConsistFilter';
import CarsFilter from './filters/CarsFilter';
import SubsystemFilter from './filters/SubsystemFilter';
import EventFilter from './filters/EventFilter';
import DateFilter from './filters/DateFilter';

import 'react-datepicker/dist/react-datepicker.css';

const optionValue = option => option?.value ?? option?.id ?? option;

const FiltersPanel = ({
  selection,
  consistOptions = [],
  carOptions = [],
  subsystemOptions = [],
  eventOptions = [],
  envDataFields = [],
  envDataFieldValues = {},
  envDataFieldMetadata = [],
  showCriteriaFilters = true,
  showConsist = true,
  showCars = true,
  showSubsystems = true,
  showEvents = true,
  showDates = true,
  showDateReset = false,
  dateRelativeLabel = 'Over the last',
  dateWindowMode = 'date',
  dateWindowValue = null,
  dateWindowUnits,
  dateWindowDefaultHours,
  dateWindowErrorId,
  validateAlarmWindow = false,
  onDateWindowModeChange = () => {},
  onDateWindowValueChange = () => {},
  onDateWindowReset = () => {},
  showEnvironmentQuery = true,
  showEnvGetDataButton = true,
  initiallyCollapseEnvironmentQuery = false,
  onSelectionChange = () => {},
  onEnvFiltersChanged = () => {},
  onEnvQueryValidityChange = () => {},
}) => {
  const filteredEvents = useMemo(() => {
    if (!selection?.subsystems?.length) return eventOptions;
    const subsystemIds = new Set(selection.subsystems.map(optionValue));
    return eventOptions.filter(event => subsystemIds.has(event.subsystemId));
  }, [eventOptions, selection?.subsystems]);

  const updateSelection = (field, value) => {
    const nextSelection = { ...selection, [field]: value };
    if (field === 'consist' && value) {
      nextSelection.cars = [];
    }
    if (field === 'subsystems' && value.length) {
      const subsystemIds = new Set(value.map(optionValue));
      nextSelection.events = (selection.events || []).filter(event => subsystemIds.has(event.subsystemId));
    }
    onSelectionChange(nextSelection);
  };

  return (
    <div className="analysis-filters-panel" data-testid="analysis-filters-panel">
      {showEnvironmentQuery && (
        <div className="chartContainer analysis-filters-panel__environment-query" data-testid="analysis-filters-panel-environment-query">
          <div className="envQueryBuilderSelection">
            <div className="chartControlTitle">
              Environment data search configuration
            </div>
            <div className="queryBuilderContainer">
              <EnvQueryBuilderView
                fields={envDataFields}
                fieldValues={envDataFieldValues}
                fieldMetadata={envDataFieldMetadata}
                value={selection?.envFilters}
                onChange={onEnvFiltersChanged}
                onValidityChange={onEnvQueryValidityChange}
                showGetDataButton={showEnvGetDataButton}
                initiallyCollapsed={initiallyCollapseEnvironmentQuery}
              />
            </div>
          </div>
        </div>
      )}
      {showCriteriaFilters && <>
        {showConsist && <div data-testid="analysis-filters-panel-consist-filter">
          <ConsistFilter options={consistOptions} value={selection?.consist} onChange={value => updateSelection('consist', value)} />
        </div>}

        {showCars && !selection?.consist && (
          <div data-testid="analysis-filters-panel-cars-filter">
            <CarsFilter options={carOptions} value={selection?.cars || []} onChange={value => updateSelection('cars', value)} />
          </div>
        )}

        {showSubsystems && (
          <div data-testid="analysis-filters-panel-subsystems-filter">
            <SubsystemFilter options={subsystemOptions} value={selection?.subsystems || []} onChange={value => updateSelection('subsystems', value)} />
          </div>
        )}

        {showEvents && (
          <div data-testid="analysis-filters-panel-events-filter">
            <EventFilter options={filteredEvents} value={selection?.events || []} onChange={value => updateSelection('events', value)} />
          </div>
        )}
      </>}
      
      {showDates && <DateFilter
        startDate={selection?.startDate}
        endDate={selection?.endDate}
        mode={dateWindowMode}
        value={dateWindowValue}
        units={dateWindowUnits}
        defaultWindowHours={dateWindowDefaultHours}
        dateWindowErrorId={dateWindowErrorId}
        dataWindowSeconds={selection?.dataWindowSeconds}
        validateAlarmWindow={validateAlarmWindow}
        onStartDateChange={date => updateSelection('startDate', date)}
        onEndDateChange={date => updateSelection('endDate', date)}
        onModeChange={onDateWindowModeChange}
        onValueChange={onDateWindowValueChange}
        onReset={onDateWindowReset}
        showDateReset={showDateReset}
        dateRelativeLabel={dateRelativeLabel}
        onInitializeRange={(start, end) => onSelectionChange({ ...selection, startDate: start, endDate: end })}
      />}
    </div>
  );
};

FiltersPanel.propTypes = {
  selection: PropTypes.object.isRequired,
  consistOptions: PropTypes.array,
  carOptions: PropTypes.array,
  subsystemOptions: PropTypes.array,
  eventOptions: PropTypes.array,
  envDataFields: PropTypes.array,
  envDataFieldValues: PropTypes.object,
  envDataFieldMetadata: PropTypes.array,
  showCriteriaFilters: PropTypes.bool,
  showConsist: PropTypes.bool,
  showCars: PropTypes.bool,
  showSubsystems: PropTypes.bool,
  showEvents: PropTypes.bool,
  showDates: PropTypes.bool,
  showDateReset: PropTypes.bool,
  dateRelativeLabel: PropTypes.string,
  dateWindowMode: PropTypes.string,
  dateWindowValue: PropTypes.number,
  dateWindowUnits: PropTypes.array,
  dateWindowDefaultHours: PropTypes.number,
  dateWindowErrorId: PropTypes.string,
  validateAlarmWindow: PropTypes.bool,
  onDateWindowModeChange: PropTypes.func,
  onDateWindowValueChange: PropTypes.func,
  onDateWindowReset: PropTypes.func,
  showEnvironmentQuery: PropTypes.bool,
  showEnvGetDataButton: PropTypes.bool,
  initiallyCollapseEnvironmentQuery: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  onEnvFiltersChanged: PropTypes.func,
  onEnvQueryValidityChange: PropTypes.func,
};

export default FiltersPanel;